/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';
import LoaderService, { IGlobalLoaderConfig } from './LoaderService';

/**
 * Custom hook to access the global loader functionality
 * Uses the event-based LoaderService under the hood
 *
 * @returns Object with loader control methods
 *
 * @example
 * ```typescript
 * const MyComponent = () => {
 *   const { showLoader, hideLoader } = useGlobalLoader();
 *
 *   const handleAction = async () => {
 *     showLoader('Loading data...');
 *     try {
 *       await fetchData();
 *     } finally {
 *       hideLoader();
 *     }
 *   };
 * };
 * ```
 */
export const useGlobalLoader = () => {
    const showLoader = React.useCallback((config?: string | IGlobalLoaderConfig) => {
        LoaderService.show(config);
    }, []);

    const hideLoader = React.useCallback((force = false) => {
        LoaderService.hide(force);
    }, []);

    const updateMessage = React.useCallback((message: string) => {
        LoaderService.updateMessage(message);
    }, []);

    return {
        showLoader,
        hideLoader,
        updateMessage
    };
};

/**
 * Hook to automatically show/hide loader based on async operation
 * @param asyncFn - Async function to execute
 * @param config - Optional loader configuration
 * @returns Object with loading state and execute function
 *
 * @example
 * ```typescript
 * const MyComponent = () => {
 *   const { isLoading, execute } = useAsyncLoader(
 *     async () => await fetchData(),
 *     'Loading data...'
 *   );
 *
 *   return <button onClick={execute} disabled={isLoading}>Load</button>;
 * };
 * ```
 */
export const useAsyncLoader = <T extends (...args: any[]) => Promise<any>>(
    asyncFn: T,
    config?: string | IGlobalLoaderConfig
): {
    isLoading: boolean;
    execute: (...args: Parameters<T>) => Promise<ReturnType<T>>;
} => {
    const { showLoader, hideLoader } = useGlobalLoader();
    const [isLoading, setIsLoading] = React.useState(false);

    const execute = React.useCallback(
        async (...args: Parameters<T>): Promise<ReturnType<T>> => {
            setIsLoading(true);
            showLoader(config);

            try {
                const result = await asyncFn(...args);
                return result;
            } finally {
                hideLoader();
                setIsLoading(false);
            }
        },
        [asyncFn, config, showLoader, hideLoader]
    );

    return { isLoading, execute };
};

/**
 * Hook to show loader during component lifecycle based on a condition
 * @param shouldShow - Condition to show the loader
 * @param config - Optional loader configuration
 *
 * @example
 * ```typescript
 * const MyComponent = ({ isLoadingData }) => {
 *   useLoaderEffect(isLoadingData, 'Loading...');
 *   return <div>Content</div>;
 * };
 * ```
 */
export const useLoaderEffect = (shouldShow: boolean, config?: string | IGlobalLoaderConfig): void => {
    const { showLoader, hideLoader } = useGlobalLoader();

    React.useEffect(() => {
        if (shouldShow) {
            showLoader(config);
        } else {
            hideLoader();
        }

        return () => {
            hideLoader();
        };
    }, [shouldShow, config, showLoader, hideLoader]);
};
